import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Grid,
  Chip,
  Avatar,
  Divider,
  Switch,
  FormControlLabel,
  CircularProgress
} from '@material-ui/core';
import Autocomplete from "@material-ui/lab/Autocomplete";
import { makeStyles } from '@material-ui/core/styles';
import { i18n } from '../../translate/i18n';
import { useKanbanTasks } from '../../hooks/useKanbanTasks';
import api from '../../services/api';
import toastUtils from '../../utils/toastUtils';

const useStyles = makeStyles(theme => ({
  section: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    marginBottom: theme.spacing(1),
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
  formControl: {
    minWidth: 120,
    marginBottom: theme.spacing(1),
  },
  chip: {
    margin: theme.spacing(0.5),
  },
  priorityChip: {
    margin: theme.spacing(0.5),
  },
  assigneeAvatar: {
    width: 24,
    height: 24,
    marginRight: theme.spacing(1),
  },
}));

const KanbanTaskModal = ({ open, onClose, ticketId, onTaskCreated, editingTask = null }) => {
  const classes = useStyles();
  const { createTask, updateTask, loading } = useKanbanTasks(ticketId);
  const [taskData, setTaskData] = useState({
    title: '',
    description: '',
    ticketId: ticketId || null,
    priority: 'medium',
    status: 'todo',
    dueDate: null,
    estimatedHours: '',
    assignees: [],
    tags: [],
    parentTaskId: null,
  });
  const [availableUsers, setAvailableUsers] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [fetchingData, setFetchingData] = useState(false);

  useEffect(() => {
    if (open) {
      fetchUsers();
      fetchTags();
      if (editingTask) {
        setTaskData({
          ...editingTask,
          dueDate: editingTask.dueDate ? new Date(editingTask.dueDate) : null,
        });
      } else {
        setTaskData({
          title: '',
          description: '',
          ticketId: ticketId || null,
          priority: 'medium',
          status: 'todo',
          dueDate: null,
          estimatedHours: '',
          assignees: [],
          tags: [],
          parentTaskId: null,
        });
      }
    }
  }, [open, editingTask, ticketId]);

  const fetchUsers = async () => {
    try {
      setFetchingData(true);
      const { data } = await api.get('/users');
      setAvailableUsers(data.users || data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toastUtils.error(i18n.t('kanban.messages.errorFetchingUsers'), 'Erro no Carregamento');
    } finally {
      setFetchingData(false);
    }
  };

  const fetchTags = async () => {
    try {
      const { data } = await api.get('/tag/kanban/');
      setAvailableTags(data.lista || data || []);
    } catch (error) {
      console.error('Error fetching tags:', error);
      toastUtils.error(i18n.t('kanban.messages.errorFetchingTags'), 'Erro no Carregamento');
    }
  };

  const handleInputChange = (field, value) => {
    setTaskData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAssigneeChange = (event, newValue) => {
    setTaskData(prev => ({
      ...prev,
      assignees: newValue,
    }));
  };

  const handleTagsChange = (event, newValue) => {
    setTaskData(prev => ({
      ...prev,
      tags: newValue,
    }));
  };

  const handleSubmit = async () => {
    if (!taskData.title.trim()) {
      toastUtils.error(i18n.t('kanban.tasks.titleRequired'), 'Título Obrigatório');
      return;
    }

    try {
      const finalTicketId = ticketId || taskData.ticketId;
      const payload = {
        ...taskData,
        dueDate: taskData.dueDate ? new Date(taskData.dueDate).toISOString() : null,
        estimatedHours: taskData.estimatedHours ? parseFloat(taskData.estimatedHours) : null,
        assigneeIds: taskData.assignees.map(user => user.id),
        tagIds: taskData.tags.map(tag => tag.id),
        ticketId: finalTicketId || null
      };

      let result;
      if (editingTask) {
        result = await updateTask(editingTask.id, payload);
      } else {
        result = await createTask(payload);
      }

      if (result) {
        onTaskCreated && onTaskCreated(result);
        handleClose();
      }
    } catch (error) {
      // Error handling is done in the hook
      console.error('Error in task modal:', error);
    }
  };

  const handleClose = () => {
    setTaskData({
      title: '',
      description: '',
      ticketId: ticketId || null,
      priority: 'medium',
      status: 'todo',
      dueDate: null,
      estimatedHours: '',
      assignees: [],
      tags: [],
      parentTaskId: null,
    });
    onClose();
  };

  const priorityOptions = [
    { value: 'low', label: i18n.t('kanban.tasks.priority.low'), color: '#4caf50' },
    { value: 'medium', label: i18n.t('kanban.tasks.priority.medium'), color: '#ff9800' },
    { value: 'high', label: i18n.t('kanban.tasks.priority.high'), color: '#f44336' },
    { value: 'urgent', label: i18n.t('kanban.tasks.priority.urgent'), color: '#9c27b0' },
  ];

  const statusOptions = [
    { value: 'todo', label: i18n.t('kanban.tasks.status.todo') },
    { value: 'in_progress', label: i18n.t('kanban.tasks.status.in_progress') },
    { value: 'done', label: i18n.t('kanban.tasks.status.done') },
    { value: 'blocked', label: i18n.t('kanban.tasks.status.blocked') },
  ];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6">
          {editingTask ? i18n.t('kanban.tasks.editTask') : i18n.t('kanban.tasks.createTask')}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>
            {i18n.t('kanban.tasks.basicInfo')}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label={i18n.t('kanban.tasks.title')}
                value={taskData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={i18n.t('kanban.tasks.description')}
                value={taskData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>{i18n.t('kanban.tasks.priority.title')}</InputLabel>
                <Select
                  value={taskData.priority}
                  onChange={(e) => handleInputChange('priority', e.target.value)}
                  label={i18n.t('kanban.tasks.priority.title')}
                >
                  {priorityOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      <Box display="flex" alignItems="center">
                        <Box 
                          width={12} 
                          height={12} 
                          borderRadius="50%" 
                          bgcolor={option.color} 
                          marginRight={1}
                        />
                        {option.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>{i18n.t('kanban.tasks.status.title')}</InputLabel>
                <Select
                  value={taskData.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  label={i18n.t('kanban.tasks.status.title')}
                >
                  {statusOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        <Divider />

        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>
            {i18n.t('kanban.tasks.scheduling')}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label={i18n.t('kanban.tasks.dueDate')}
                type="date"
                value={taskData.dueDate ? taskData.dueDate.toISOString().split('T')[0] : ''}
                onChange={(e) => handleInputChange('dueDate', e.target.value ? new Date(e.target.value) : null)}
                variant="outlined"
                size="small"
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label={i18n.t('kanban.tasks.estimatedHours')}
                value={taskData.estimatedHours}
                onChange={(e) => handleInputChange('estimatedHours', e.target.value)}
                type="number"
                variant="outlined"
                size="small"
                fullWidth
                inputProps={{ min: 0, step: 0.5 }}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider />

        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>
            {i18n.t('kanban.tasks.assignment')}
          </Typography>
          <Autocomplete
            multiple
            options={availableUsers}
            value={taskData.assignees}
            onChange={handleAssigneeChange}
            getOptionLabel={(user) => user.name}
            renderOption={(user) => (
              <Box display="flex" alignItems="center">
                <Avatar src={user.profileImage} className={classes.assigneeAvatar}>
                  {user.name.charAt(0)}
                </Avatar>
                {user.name}
              </Box>
            )}
            renderTags={(value, getTagProps) =>
              value.map((user, index) => (
                <Chip
                  avatar={<Avatar src={user.profileImage}>{user.name.charAt(0)}</Avatar>}
                  label={user.name}
                  size="small"
                  {...getTagProps({ index })}
                  className={classes.chip}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                size="small"
                label={i18n.t('kanban.tasks.assignees')}
                placeholder={i18n.t('kanban.tasks.selectAssignees')}
              />
            )}
          />
        </Box>

        <Divider />

        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>
            {i18n.t('kanban.tasks.tags')}
          </Typography>
          <Autocomplete
            multiple
            options={availableTags}
            value={taskData.tags}
            onChange={handleTagsChange}
            getOptionLabel={(tag) => tag.name}
            renderOption={(tag) => (
              <Box display="flex" alignItems="center">
                <Box 
                  width={12} 
                  height={12} 
                  borderRadius="50%" 
                  bgcolor={tag.color || '#757575'} 
                  marginRight={1}
                />
                {tag.name}
              </Box>
            )}
            renderTags={(value, getTagProps) =>
              value.map((tag, index) => (
                <Chip
                  label={tag.name}
                  size="small"
                  style={{ backgroundColor: tag.color || '#757575', color: 'white' }}
                  {...getTagProps({ index })}
                  className={classes.chip}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                size="small"
                label={i18n.t('kanban.tasks.tags')}
                placeholder={i18n.t('kanban.tasks.selectTags')}
              />
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading || fetchingData}>
          {i18n.t('kanban.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={loading || fetchingData || !taskData.title.trim()}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {loading ? 
            i18n.t('kanban.saving') : 
            (editingTask ? i18n.t('kanban.tasks.updateTask') : i18n.t('kanban.tasks.createTask'))
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default KanbanTaskModal;